// The chat side of the task tools (#11942, CD ruling 6): the agentic chat
// creates, edits and deletes tasks through four hosted MCP tools, and the
// transcript answers with a task card rather than prose. The tool result
// reaches the frontend only as `outputPreview` — the stringified return,
// JSON from the SDK path or Python `str(dict)` from the harness path, cut at
// 2000 chars — so everything here reads fields with tolerant regexes and
// treats a missing field as unknown rather than failing the whole card.

export type TaskToolKind = "list" | "create" | "update" | "delete";

const TOOL_KINDS: Record<string, TaskToolKind> = {
  list_tasks: "list",
  create_task: "create",
  update_task: "update",
  delete_task: "delete",
};

/** Which task tool a tool step is, matched on the bare suffix — the name
 * arrives prefixed (`mcp__finpilot__create_task`) or bare. */
export function taskToolKind(
  toolName: string | undefined
): TaskToolKind | null {
  if (!toolName) return null;
  const suffix = toolName.split("__").pop() ?? "";
  return TOOL_KINDS[suffix] ?? null;
}

export interface TaskRecordRef {
  kind: string | null;
  publicId: string | null;
  name: string | null;
}

/** A task as the result names it; null where the preview did not say. */
export interface ParsedTask {
  taskId: string;
  listId: string | null;
  title: string | null;
  detail: string | null;
  assigneeName: string | null;
  assigneeUserId: string | null;
  /** A calendar day, never a timestamp. */
  dueDay: string | null;
  status: string | null;
  completedOn: string | null;
  record: TaskRecordRef | null;
}

export interface TaskFieldChange {
  from: string | null;
  to: string | null;
}

export interface TaskChanges {
  assignee?: TaskFieldChange;
  due_day?: TaskFieldChange;
  status?: TaskFieldChange;
  title?: TaskFieldChange;
}

/** What a delete result offers back, so Undo can recreate the task. */
export interface TaskUndo {
  /** The undo block as the preview carried it. */
  raw: string;
  title: string | null;
  detail: string | null;
  assigneeUserId: string | null;
  dueDay: string | null;
  listId: string | null;
  record: TaskRecordRef | null;
}

export type TaskToolResult =
  | {
      kind: "create";
      /** False when the tool refused to duplicate an open task; `task` is
       * then the existing one. */
      created: boolean;
      task: ParsedTask;
      checkedDocuments: number | null;
      checkedOpenTasks: number | null;
    }
  | { kind: "update"; task: ParsedTask; changes: TaskChanges }
  | {
      kind: "delete";
      taskId: string;
      title: string | null;
      undo: TaskUndo | null;
    };

// ------------------------------------------------------------- readers --

const key = (field: string): string => `["']${field}["']\\s*:\\s*`;

// A quoted string in either serialization, with its escapes.
const STRING = `(?:"((?:[^"\\\\]|\\\\.)*)"|'((?:[^'\\\\]|\\\\.)*)')`;

const unescapeString = (raw: string): string =>
  raw.replace(/\\(.)/g, (_, ch: string) => (ch === "n" ? "\n" : ch));

/** The body of the object between the brace `start` opens and its match —
 * or to the end of a preview cut mid-object. */
const objectBodyFrom = (text: string, start: number): string => {
  let depth = 1;
  for (let i = start; i < text.length; i++) {
    const ch = text[i];
    if (ch === "{") depth += 1;
    else if (ch === "}") {
      depth -= 1;
      if (depth === 0) return text.slice(start, i);
    }
  }
  return text.slice(start);
};

/** The body of the object a key opens; null when the key is absent or its
 * value is not an object (`null` / `None`). */
const objectAfter = (text: string, field: string): string | null => {
  const match = new RegExp(`${key(field)}\\{`).exec(text);
  if (!match) return null;
  return objectBodyFrom(text, match.index + match[0].length);
};

/** The body of the first object in the list a key opens. */
const firstObjectInList = (text: string, field: string): string | null => {
  const match = new RegExp(`${key(field)}\\[\\s*\\{`).exec(text);
  if (!match) return null;
  return objectBodyFrom(text, match.index + match[0].length);
};

/** A string value; null when absent, null-valued, or cut before it closed
 * (half a title is worse than the tool call's own). */
const stringField = (text: string, field: string): string | null => {
  const match = new RegExp(`${key(field)}${STRING}`).exec(text);
  if (!match) return null;
  return unescapeString(match[1] ?? match[2] ?? "");
};

/** A `YYYY-MM-DD` value; null otherwise. */
const dayField = (text: string, field: string): string | null => {
  const match = new RegExp(`${key(field)}["'](\\d{4}-\\d{2}-\\d{2})["']`).exec(
    text
  );
  return match ? match[1] : null;
};

const numberField = (text: string, field: string): number | null => {
  const match = new RegExp(`${key(field)}(\\d+)`).exec(text);
  return match ? Number(match[1]) : null;
};

const boolField = (text: string, field: string): boolean | null => {
  const match = new RegExp(`${key(field)}(true|True|false|False)`).exec(text);
  if (!match) return null;
  return match[1] === "true" || match[1] === "True";
};

/** A scalar that may legitimately be null: `undefined` when the key is
 * absent, `null` for `null` / `None`, else the value as text. A nested
 * object (an assignee spelled out rather than named) reads as its name. */
const scalarField = (
  text: string,
  field: string
): string | null | undefined => {
  const match = new RegExp(
    `${key(field)}(?:(null|None)|${STRING}|(true|True|false|False|-?\\d+(?:\\.\\d+)?))`
  ).exec(text);
  if (match) {
    if (match[1]) return null;
    if (match[4] !== undefined) return match[4];
    return unescapeString(match[2] ?? match[3] ?? "");
  }
  const nested = objectAfter(text, field);
  if (nested) return stringField(nested, "name");
  return undefined;
};

const recordFrom = (body: string | null): TaskRecordRef | null =>
  body
    ? {
        kind: stringField(body, "kind"),
        publicId: stringField(body, "public_id"),
        name: stringField(body, "name"),
      }
    : null;

/** A task object's fields; null without a task id (nothing to act on). */
export function parseTaskObject(body: string): ParsedTask | null {
  const taskId = stringField(body, "task_id");
  if (!taskId) return null;
  const assignee = objectAfter(body, "assignee");
  return {
    taskId,
    listId: stringField(body, "list_id"),
    title: stringField(body, "title"),
    detail: scalarField(body, "detail") ?? null,
    assigneeName: assignee ? stringField(assignee, "name") : null,
    assigneeUserId: assignee ? stringField(assignee, "user_id") : null,
    dueDay: dayField(body, "due_day"),
    status: stringField(body, "status"),
    completedOn: dayField(body, "completed_on"),
    record: recordFrom(objectAfter(body, "record")),
  };
}

const CHANGE_FIELDS = ["assignee", "due_day", "status", "title"] as const;

/** The `changes` block of an update result: each field that moved, with
 * null tolerated on either side. */
export function parseTaskChanges(preview: string): TaskChanges {
  const body = objectAfter(preview, "changes");
  const changes: TaskChanges = {};
  if (!body) return changes;
  for (const field of CHANGE_FIELDS) {
    const change = objectAfter(body, field);
    if (!change) continue;
    const from = scalarField(change, "from");
    const to = scalarField(change, "to");
    if (from === undefined && to === undefined) continue;
    changes[field] = { from: from ?? null, to: to ?? null };
  }
  return changes;
}

const parseCreate = (
  preview: string,
  inputTitle: string | null
): TaskToolResult | null => {
  const created = boolField(preview, "created");
  const madeBody = created === false ? null : objectAfter(preview, "task");
  const body = madeBody ?? firstObjectInList(preview, "existing_open_tasks");
  if (!body) return null;
  const task = parseTaskObject(body);
  if (!task) return null;
  const checked = objectAfter(preview, "checked");
  return {
    kind: "create",
    created: madeBody !== null,
    task: { ...task, title: task.title ?? inputTitle },
    checkedDocuments: checked ? numberField(checked, "documents") : null,
    checkedOpenTasks: checked ? numberField(checked, "open_tasks") : null,
  };
};

const parseUpdate = (
  preview: string,
  inputTitle: string | null
): TaskToolResult | null => {
  const body = objectAfter(preview, "task");
  if (!body) return null;
  const task = parseTaskObject(body);
  if (!task) return null;
  const changes = parseTaskChanges(preview);
  return {
    kind: "update",
    task: { ...task, title: task.title ?? changes.title?.to ?? inputTitle },
    changes,
  };
};

const parseDelete = (
  preview: string,
  inputTitle: string | null
): TaskToolResult | null => {
  const taskId = stringField(preview, "task_id");
  if (!taskId || boolField(preview, "deleted") === false) return null;
  const undoBody = objectAfter(preview, "undo");
  const undo: TaskUndo | null = undoBody
    ? {
        raw: undoBody,
        title: stringField(undoBody, "title"),
        detail: scalarField(undoBody, "detail") ?? null,
        assigneeUserId: stringField(undoBody, "assignee_user_id"),
        dueDay: dayField(undoBody, "due_day"),
        listId: stringField(undoBody, "list_id"),
        record: recordFrom(objectAfter(undoBody, "record")),
      }
    : null;
  return {
    kind: "delete",
    taskId,
    title: undo?.title ?? inputTitle,
    undo,
  };
};

/**
 * What a task tool's result says the card should show. Null when there is
 * nothing to show — no preview, no task id, or a delete that deleted
 * nothing — and the segment is dropped. `inputTitle` is the title on the
 * tool call's input, the stand-in when the preview was cut before the
 * result's own.
 */
export function parseTaskToolResult(
  kind: Exclude<TaskToolKind, "list">,
  outputPreview: string | undefined,
  inputTitle: string | null
): TaskToolResult | null {
  if (!outputPreview) return null;
  switch (kind) {
    case "create":
      return parseCreate(outputPreview, inputTitle);
    case "update":
      return parseUpdate(outputPreview, inputTitle);
    case "delete":
      return parseDelete(outputPreview, inputTitle);
  }
}
